import { queryOptions, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { StyleId, AspectRatio } from '@/lib/constants'

/** 템플릿 레퍼런스 이미지 */
export interface TemplateReferenceImage {
  s3Key: string
  thumbnailS3Key: string
  thumbnailUrl: string
}

/** 템플릿 응답 */
export interface Template {
  id: string
  name: string
  description: string | null
  /** 언어 코드 → 번역된 이름 (ko 원문은 name) */
  nameI18n?: Record<string, string> | null
  /** 언어 코드 → 번역된 설명 (ko 원문은 description) */
  descriptionI18n?: Record<string, string> | null
  textPrompt: string
  /** 언어 코드 → 번역된 프롬프트 (ko 원문은 textPrompt) */
  textPromptI18n?: Record<string, string> | null
  styleId: StyleId
  promptSuffix: string | null
  modelId: string | null
  aspectRatio: AspectRatio
  thumbnailUrl: string | null
  updatedAt: string
  referenceImages: TemplateReferenceImage[]
}

/** 카테고리별 템플릿 그룹 */
export interface TemplateCategoryGroup {
  id: string
  name: string
  description: string | null
  /** 언어 코드 → 번역된 이름 (ko 원문은 name) */
  nameI18n?: Record<string, string> | null
  /** 언어 코드 → 번역된 설명 (ko 원문은 description) */
  descriptionI18n?: Record<string, string> | null
  templates: Template[]
}

/**
 * 카테고리별 그룹핑된 템플릿 목록 조회
 */
export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<TemplateCategoryGroup[]>('/templates'),
    staleTime: 1000 * 60 * 5, // 5분
  })
}

/** 인기 템플릿 (원래 소속 카테고리 정보 포함) */
export interface PopularTemplate extends Template {
  categoryId: string
  categoryName: string
  /** 언어 코드 → 번역된 카테고리 이름 (ko 원문은 categoryName) */
  categoryNameI18n?: Record<string, string> | null
}

/**
 * 인기 템플릿 목록 조회 (사용 횟수 순)
 */
export function usePopularTemplates(enabled: boolean = true) {
  return useQuery({
    queryKey: ['templates', 'popular'],
    queryFn: () => api.get<PopularTemplate[]>('/templates/popular'),
    staleTime: 1000 * 60 * 5,
    enabled,
  })
}

/** 특정 템플릿 상세 쿼리 (useTemplate 과 queryClient.fetchQuery 가 같은 캐시를 쓴다) */
export function templateQueryOptions(id: string | null) {
  return queryOptions({
    queryKey: ['template', id],
    queryFn: () => api.get<Template>(`/templates/${id}`),
    staleTime: 1000 * 60 * 5,
  })
}

/**
 * 특정 템플릿 상세 조회
 */
export function useTemplate(id: string | null) {
  return useQuery({ ...templateQueryOptions(id), enabled: !!id })
}
